import * as React from "react"
import { DeliveryContact } from "../components/DeliveryContact"
import { QuantityStepper } from "../components/QuantityStepper"
import { Icon } from "../components/Icon"
import { Unavailable } from "../components/StatusMessages"
import { HireDateFields } from "../components/HireDateFields"
import { POSTCODE_VALIDATION_MESSAGE } from "../dateSelection"
import type { CheckoutViewModel } from "../viewTypes"

type Props = Pick<CheckoutViewModel,
    "deliveryContactRequired" | "product" | "props" | "instanceId" | "dateNotice" | "meta" | "quantity" | "changeQuantity" | "stockCap" | "postcode" | "setPostcode" | "clearBookingResult" | "normalizedPostcode" | "calendarSnapshot" | "calendarPhase" | "calendarOpen" | "calendarFeedback" | "activeDateField" | "openDatePicker" | "range" | "isMobile" | "resetDatesButton" | "pricePromise" | "calendar" | "availability" | "error" | "bookingDisabled" | "selectionChecking" | "continueFromSelection" | "selectionContinueLabel">

export function DatesStep(inputs: Props) {
    const {
        instanceId,
        deliveryContactRequired,
        product,
        props,
        dateNotice,
        meta,
        quantity,
        changeQuantity,
        stockCap,
        postcode,
        setPostcode,
        clearBookingResult,
        normalizedPostcode,
        calendarSnapshot,
        calendarPhase,
        calendarOpen,
        calendarFeedback,
        activeDateField,
        openDatePicker,
        range,
        isMobile,
        resetDatesButton,
        pricePromise,
        calendar,
        availability,
        error,
        bookingDisabled,
        selectionChecking,
        continueFromSelection,
        selectionContinueLabel,
    } = inputs
    const [postcodeBlurred, setPostcodeBlurred] = React.useState(false)
    const postcodeNotice = dateNotice?.target === "postcode" ? dateNotice : null
    const postcodeErrorId = `${instanceId}-postcode-error`
    const hasPostcode = Boolean(normalizedPostcode)
    const postcodeInvalid = (!hasPostcode && (postcodeBlurred || Boolean(postcodeNotice))) || error === POSTCODE_VALIDATION_MESSAGE
    const postcodeConfirmed = !deliveryContactRequired && !postcodeInvalid && hasPostcode && calendarSnapshot?.postcode === normalizedPostcode
        && calendarSnapshot.productSlug === meta.slug && calendarPhase !== "error" && !error
    const checkingPostcode = !deliveryContactRequired && !postcodeInvalid && hasPostcode && calendarSnapshot?.postcode !== normalizedPostcode && calendarPhase !== "error"
    return (
        <section className="ah-panel ah-stage-panel ah-postcode-panel">
            <fieldset className="ah-product-picker" aria-labelledby={`${instanceId}-equipment-title`}>
                <legend className="ah-equipment-heading">
                    <span id={`${instanceId}-equipment-title`} className="ah-section-title">Equipment</span>
                </legend>
                <div className="ah-product-selected">
                    <img src={meta.image} alt="" />
                    <span><strong>{meta.name}</strong></span>
                    <QuantityStepper quantity={quantity} maximum={stockCap} onChange={changeQuantity} />
                </div>
            </fieldset>

            <div className="ah-section-divider" />
            <h3 className="ah-section-title">1. Enter your delivery postcode</h3>
            <div className="ah-field ah-postcode-field">
                <input aria-label="Delivery postcode" aria-busy={checkingPostcode} aria-invalid={postcodeInvalid} aria-describedby={postcodeInvalid ? postcodeErrorId : deliveryContactRequired ? `${instanceId}-delivery-contact` : undefined} value={postcode} onBlur={() => setPostcodeBlurred(true)} onChange={(event) => { setPostcodeBlurred(false); setPostcode(event.target.value); clearBookingResult() }} inputMode="text" autoComplete="postal-code" placeholder="E.g. SE23 3XX" />
                {checkingPostcode && <span className="ah-postcode-spinner" aria-hidden="true" />}
            </div>
            {postcodeInvalid && <p id={postcodeErrorId} className="ah-postcode-error" role="alert"><Icon name="warning" />{POSTCODE_VALIDATION_MESSAGE}</p>}
            {deliveryContactRequired ? <DeliveryContact id={`${instanceId}-delivery-contact`} postcode={normalizedPostcode}
                productName={meta.name} quantity={quantity} whatsapp={product?.support.whatsapp || props.supportWhatsApp} /> : <>
            {postcodeConfirmed && <p className="ah-postcode-confirmation" role="status"><Icon name="check-circle" />Great news! We deliver 7 days a week in your area.</p>}
            {hasPostcode && !calendarOpen && calendarFeedback}
            <div className="ah-section-divider" />
            <div className="ah-dates-heading">
                <h3 className="ah-section-title">2. Select your hire dates</h3>
                {!isMobile && hasPostcode && resetDatesButton}
            </div>
            <HireDateFields range={range} activeDateField={isMobile || !hasPostcode ? null : activeDateField} postcodeRequired={!hasPostcode} onSelect={(field) => { if (!hasPostcode) setPostcodeBlurred(true); openDatePicker(field) }} />
            {!isMobile && <div className={`ah-dates-reveal${hasPostcode ? " is-open" : ""}`} aria-hidden={!hasPostcode} {...(hasPostcode ? {} : { inert: "" })}>
                <div className="ah-dates-reveal-clip">
                    <div className="ah-dates-reveal-content">
                        {pricePromise}
                        <div className="ah-calendar-wrap ah-calendar-desktop">{calendar}</div>
                    </div>
                </div>
            </div>}

            {availability && !availability.available && <Unavailable availability={availability} />}
            {error && error !== POSTCODE_VALIDATION_MESSAGE && <div className="ah-alert ah-error" role="alert">{error}</div>}
            <button type="button" className="ah-primary-button ah-stage-action ah-selection-continue" disabled={bookingDisabled} aria-busy={selectionChecking} onClick={() => void continueFromSelection()}>{selectionContinueLabel}</button>
            </>}
        </section>
    )
}
